import { erroPeriodoConsulta } from '../../../core/tools/periodo-consulta';
import { Component, OnInit } from '@angular/core';
import { Animacao } from '../../../core/tools/animacao';
import { ExcelService } from '../../../core/tools/excel.service';
import {
  DashboardVendaInternaCliente,
  DashboardVendaInternaSegmento,
  DashboardVendaInternaDia,
  DashboardVendaInternaFaturamento,
  DashboardVendaInternaGrupo,
  DashboardVendaInternaLigacao,
  DashboardVendaInternaService,
  DashboardVendaInternaTipoInterna,
  DashboardVendaInternaVendedorExterno
} from '../../../core/services/dashboard/venda-interna/dashboard-venda-interna.service';

@Component({
  selector: 'app-dashboard-venda-interna',
  templateUrl: './dashboard-venda-interna.component.html',
  styleUrls: ['./dashboard-venda-interna.component.css'],
  preserveWhitespaces: false,
  animations: [Animacao],
  standalone: false
})
export class DashboardVendaInternaComponent implements OnInit {
  erroPeriodo = '';
  segmentos: DashboardVendaInternaSegmento[] = [];
  carregando: boolean = false;
  dataInicio: string;
  dataFim: string;
  tipoDados: 'FATURADO' | 'GERAL' = 'FATURADO';
  ordenacaoFaturamento: 'valor' | 'ligacoes' | 'litros' | 'pedidos' = 'valor';
  ordenacaoExternos: 'valor' | 'ligacoes' | 'litros' | 'pedidos' = 'valor';
  ordenacaoClientes: 'valor' | 'ligacoes' | 'litros' | 'pedidos' = 'valor';
  filtroInternaExterno: number = null;
  filtroInternaLigacao: number = null;
  filtroResultadoLigacao: string = '';

  dias: DashboardVendaInternaDia[] = [];
  tipos: DashboardVendaInternaGrupo[] = [];
  resultados: DashboardVendaInternaGrupo[] = [];
  ligacoes: DashboardVendaInternaLigacao[] = [];
  faturamentoInterno: DashboardVendaInternaFaturamento[] = [];
  vendedoresExternos: DashboardVendaInternaVendedorExterno[] = [];
  tiposPorInterna: DashboardVendaInternaTipoInterna[] = [];
  tiposPorInternaGrupos: Array<{ vendInterna: string, itens: DashboardVendaInternaTipoInterna[] }> = [];
  clientes: DashboardVendaInternaCliente[] = [];

  constructor(
    private dashboardVendaInternaService: DashboardVendaInternaService,
    private excelService: ExcelService
  ) {
    const hoje = new Date();
    this.dataInicio = this.formatarDataApi(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 6));
    this.dataFim = this.formatarDataApi(hoje);
  }

  ngOnInit() {
    this.atualizar();
  }

  atualizar() {
    this.erroPeriodo = erroPeriodoConsulta(this.dataInicio, this.dataFim);
    if (this.erroPeriodo) return;
    this.carregando = true;
    this.dashboardVendaInternaService.getResumo({
      userId: parseInt(localStorage.getItem('id')),
      dataInicio: this.dataInicio,
      dataFim: this.dataFim,
      tipoDados: this.tipoDados
    }).subscribe(resumo => {
      this.segmentos = this.listaValida(resumo.segmentos);
      this.dias = this.listaValida(resumo.dias);
      this.tipos = this.listaValida(resumo.tipos);
      this.resultados = this.listaValida(resumo.resultados);
      this.ligacoes = this.listaValida(resumo.ligacoes);
      this.faturamentoInterno = this.listaValida(resumo.faturamentoInterno);
      this.vendedoresExternos = this.listaValida(resumo.vendedoresExternos);
      this.tiposPorInterna = this.listaValida(resumo.tiposPorInterna);
      this.tiposPorInternaGrupos = this.montarTiposPorInternaGrupos();
      this.clientes = this.listaValida(resumo.clientes);
      this.carregando = false;
    }, () => this.carregando = false);
  }

  listaValida<T>(lista: T[]): T[] {
    return (lista || []).filter(item => item != null);
  }

  totalLigacoes(): number {
    return this.dias.reduce((sum, item) => sum + item.total, 0);
  }

  totalPedidosInternosDia(): number {
    return this.dias.reduce((sum, item) => sum + item.pedidos, 0);
  }

  conversaoGeral(): number {
    const total = this.totalLigacoes();
    return total > 0 ? (this.totalPedidosInternosDia() / total) * 100 : 0;
  }

  totalVendasReais(): number {
    return this.faturamentoInterno.reduce((sum, item) => sum + item.vendasReais, 0);
  }

  totalLitros(): number {
    return this.faturamentoInterno.reduce((sum, item) => sum + item.litros, 0);
  }

  totalPedidos(): number {
    return this.faturamentoInterno.reduce((sum, item) => sum + item.pedidos, 0);
  }

  faturamentoInternoOrdenado(): DashboardVendaInternaFaturamento[] {
    return [...this.faturamentoInterno].sort((a, b) => this.compararFaturamento(b, a));
  }

  internasFiltroExterno(): { id: number, nome: string }[] {
    const mapa: { [id: number]: string } = {};

    this.vendedoresExternos.forEach(item => {
      if (item.sellerIdInterna) {
        mapa[item.sellerIdInterna] = item.vendInterna || '';
      }
    });

    return Object.keys(mapa)
      .map(id => ({ id: parseInt(id), nome: mapa[parseInt(id)] }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }

  vendedoresExternosFiltrados(): DashboardVendaInternaVendedorExterno[] {
    return this.listaValida(this.vendedoresExternos)
      .filter(item => !this.filtroInternaExterno || item.sellerIdInterna == this.filtroInternaExterno)
      .sort((a, b) => this.compararExternos(b, a));
  }

  totalExternoLigacoes(): number {
    return this.vendedoresExternosFiltrados().reduce((sum, item) => sum + (item.ligacoes || 0), 0);
  }

  totalExternoVendasReais(): number {
    return this.vendedoresExternosFiltrados().reduce((sum, item) => sum + (item.vendasReais || 0), 0);
  }

  totalExternoLitros(): number {
    return this.vendedoresExternosFiltrados().reduce((sum, item) => sum + (item.litros || 0), 0);
  }

  totalExternoPedidos(): number {
    return this.vendedoresExternosFiltrados().reduce((sum, item) => sum + (item.pedidos || 0), 0);
  }

  tiposPorInternaOrdenados(): DashboardVendaInternaTipoInterna[] {
    return this.listaValida(this.tiposPorInterna).sort((a, b) => {
      const interna = (a.vendInterna || '').localeCompare(b.vendInterna || '');

      if (interna != 0) {
        return interna;
      }

      return (a.tipo || '').localeCompare(b.tipo || '');
    });
  }

  montarTiposPorInternaGrupos(): Array<{ vendInterna: string, itens: DashboardVendaInternaTipoInterna[] }> {
    const grupos: { [vendInterna: string]: DashboardVendaInternaTipoInterna[] } = {};

    this.tiposPorInternaOrdenados().forEach(item => {
      const vendInterna = item.vendInterna || 'Sem vendedora';

      if (grupos[vendInterna] == null) {
        grupos[vendInterna] = [];
      }

      grupos[vendInterna].push(item);
    });

    return Object.keys(grupos)
      .sort((a, b) => a.localeCompare(b))
      .map(vendInterna => ({ vendInterna, itens: grupos[vendInterna] }));
  }

  totalTipoInterna(item: DashboardVendaInternaTipoInterna): number {
    return (item.vendas || 0) + (item.naoVendas || 0);
  }

  totalGrupoTipoInterna(grupo: { vendInterna: string, itens: DashboardVendaInternaTipoInterna[] }, campo: 'total' | 'naoVendas' | 'vendas' | 'vendasReais' | 'litros'): number {
    const itens = this.listaValida(grupo ? grupo.itens : []);

    return itens.reduce((sum, item) => {
      if (campo == 'total') {
        return sum + this.totalTipoInterna(item);
      }

      return sum + (item[campo] || 0);
    }, 0);
  }

  clientesOrdenados(): DashboardVendaInternaCliente[] {
    return [...this.clientes].sort((a, b) => this.compararClientes(b, a));
  }

  resultadosFiltroLigacoes(): string[] {
    const mapa: { [resultado: string]: boolean } = {};

    this.ligacoes.forEach(item => {
      const resultado = item.resultado || 'Sem resultado';
      mapa[resultado] = true;
    });

    return Object.keys(mapa).sort((a, b) => a.localeCompare(b));
  }

  internasFiltroLigacoes(): { id: number, nome: string }[] {
    const mapa: { [id: number]: string } = {};

    this.ligacoes.forEach(item => {
      if (item.sellerIdInterna) {
        mapa[item.sellerIdInterna] = item.vendInterna || '';
      }
    });

    return Object.keys(mapa)
      .map(id => ({ id: parseInt(id), nome: mapa[parseInt(id)] }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }

  ligacoesFiltradas(): DashboardVendaInternaLigacao[] {
    let ligacoes = this.ligacoes;

    if (this.filtroInternaLigacao) {
      ligacoes = ligacoes.filter(item => item.sellerIdInterna == this.filtroInternaLigacao);
    }

    if (this.filtroResultadoLigacao) {
      const filtro = this.normalizar(this.filtroResultadoLigacao);
      ligacoes = ligacoes.filter(item => this.normalizar(item.resultado || 'Sem resultado') == filtro);
    }

    return this.ordenarLigacoes(ligacoes);
  }

  exportarLigacoesExcel() {
    const dados = this.ligacoesFiltradas().map(ligacao => ({
      'Vend interna': ligacao.vendInterna || '',
      'Vend externo': ligacao.vendExterno || '',
      'Tipo': ligacao.tipo || '',
      'Origem': this.origemTexto(ligacao.origem),
      'Status': this.statusTexto(ligacao.status),
      'Data': ligacao.dataLigacao || '',
      'Cod ERP': ligacao.codigoErpCliente || '',
      'Cliente': ligacao.cliente || '',
      'Cidade': ligacao.cidade || '',
      'Check-in': ligacao.checkIn || '',
      'Check-out': ligacao.checkOut || '',
      'Duracao': ligacao.duracao != null ? ligacao.duracao : '',
      'Resultado': ligacao.resultado || '',
      'Observacao': ligacao.observacao || ''
    }));

    this.excelService.exportAsExcelFile(dados, 'Acompanhamento das ligacoes');
  }

  maxVendasReaisDia(): number {
    return Math.max(0, ...this.dias.map(item => item.vendasReais));
  }

  maxLitrosDia(): number {
    return Math.max(0, ...this.dias.map(item => item.litros));
  }

  maxDia(): number {
    return Math.max(0, ...this.dias.map(item => item.total));
  }

  maxGrupo(itens: DashboardVendaInternaGrupo[]): number {
    return Math.max(0, ...itens.map(item => item.quantidade));
  }

  largura(valor: number, maximo: number): number {
    return maximo > 0 ? Math.max((valor / maximo) * 100, 4) : 0;
  }

  altura(valor: number, maximo: number): number {
    return maximo > 0 ? Math.max((valor / maximo) * 100, 8) : 0;
  }

  larguraPedidosDia(dia: DashboardVendaInternaDia): number {
    if (dia.total <= 0) {
      return 0;
    }

    return Math.min((dia.pedidos / dia.total) * 100, 100);
  }

  larguraSemPedidoDia(dia: DashboardVendaInternaDia): number {
    if (dia.total <= 0) {
      return 0;
    }

    return Math.max(100 - this.larguraPedidosDia(dia), 0);
  }

  tipoClasse(tipo: string): string {
    const valor = this.normalizar(tipo);

    if (valor.indexOf('email') > -1 || valor.indexOf('e-mail') > -1 || valor == 'e') {
      return 'dashboard-venda-interna-tipo-email';
    }

    if (valor.indexOf('feita') > -1 || valor == 'f') {
      return 'dashboard-venda-interna-tipo-feita';
    }

    if (valor.indexOf('recebida') > -1 || valor == 'r') {
      return 'dashboard-venda-interna-tipo-recebida';
    }

    if (valor.indexOf('whatsapp') > -1 || valor.indexOf('zap') > -1 || valor == 'w') {
      return 'dashboard-venda-interna-tipo-whatsapp';
    }

    return 'dashboard-venda-interna-tipo-feita';
  }

  statusClasse(status: string): string {
    const valor = this.normalizar(this.statusTexto(status));

    if (valor.indexOf('venda realizada') > -1) {
      return 'dashboard-venda-interna-status-success';
    }

    if (valor.indexOf('nao comprou') > -1) {
      return 'dashboard-venda-interna-status-danger';
    }

    return 'dashboard-venda-interna-status-warning';
  }

  statusTexto(status: string): string {
    return status || '-';
  }

  origemTexto(origem: string): string {
    return (origem || '').trim() || 'Não identificado';
  }

  private normalizar(valor: string): string {
    return (valor || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private compararFaturamento(a: DashboardVendaInternaFaturamento, b: DashboardVendaInternaFaturamento): number {
    switch (this.ordenacaoFaturamento) {
      case 'ligacoes':
        return (a.ligacoes || 0) - (b.ligacoes || 0) || (a.vendasReais || 0) - (b.vendasReais || 0);
      case 'litros':
        return (a.litros || 0) - (b.litros || 0) || (a.vendasReais || 0) - (b.vendasReais || 0);
      case 'pedidos':
        return (a.pedidos || 0) - (b.pedidos || 0) || (a.vendasReais || 0) - (b.vendasReais || 0);
      default:
        return (a.vendasReais || 0) - (b.vendasReais || 0) || (a.pedidos || 0) - (b.pedidos || 0);
    }
  }

  private compararExternos(a: DashboardVendaInternaVendedorExterno, b: DashboardVendaInternaVendedorExterno): number {
    switch (this.ordenacaoExternos) {
      case 'ligacoes':
        return (a.ligacoes || 0) - (b.ligacoes || 0) || (a.vendasReais || 0) - (b.vendasReais || 0);
      case 'litros':
        return (a.litros || 0) - (b.litros || 0) || (a.vendasReais || 0) - (b.vendasReais || 0);
      case 'pedidos':
        return (a.pedidos || 0) - (b.pedidos || 0) || (a.vendasReais || 0) - (b.vendasReais || 0);
      default:
        return (a.vendasReais || 0) - (b.vendasReais || 0) || (a.pedidos || 0) - (b.pedidos || 0);
    }
  }

  private compararClientes(a: DashboardVendaInternaCliente, b: DashboardVendaInternaCliente): number {
    switch (this.ordenacaoClientes) {
      case 'ligacoes':
        return (a.ligacoes || 0) - (b.ligacoes || 0) || (a.vendasReais || 0) - (b.vendasReais || 0);
      case 'litros':
        return (a.litros || 0) - (b.litros || 0) || (a.vendasReais || 0) - (b.vendasReais || 0);
      case 'pedidos':
        return (a.pedidos || 0) - (b.pedidos || 0) || (a.vendasReais || 0) - (b.vendasReais || 0);
      default:
        return (a.vendasReais || 0) - (b.vendasReais || 0) || (a.pedidos || 0) - (b.pedidos || 0);
    }
  }

  private ordenarLigacoes(ligacoes: DashboardVendaInternaLigacao[]): DashboardVendaInternaLigacao[] {
    return [...ligacoes].sort((a, b) => {
      const vendInterna = (a.vendInterna || '').localeCompare(b.vendInterna || '');

      if (vendInterna != 0) {
        return vendInterna;
      }

      const dataA = this.obterTimestamp(a.checkIn || a.dataLigacao);
      const dataB = this.obterTimestamp(b.checkIn || b.dataLigacao);

      if (dataA != dataB) {
        return dataB - dataA;
      }

      return (a.vendExterno || '').localeCompare(b.vendExterno || '');
    });
  }

  private obterTimestamp(valor: string): number {
    if (!valor) {
      return 0;
    }

    const partes = valor.toString().match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/);

    if (partes != null) {
      const dia = parseInt(partes[1], 10);
      const mes = parseInt(partes[2], 10) - 1;
      const ano = parseInt(partes[3], 10);
      const hora = partes[4] != null ? parseInt(partes[4], 10) : 0;
      const minuto = partes[5] != null ? parseInt(partes[5], 10) : 0;
      const segundo = partes[6] != null ? parseInt(partes[6], 10) : 0;

      return new Date(ano, mes, dia, hora, minuto, segundo).getTime();
    }

    const data = new Date(valor);

    return isNaN(data.getTime()) ? 0 : data.getTime();
  }

  private formatarDataApi(data: Date): string {
    const ano = data.getFullYear();
    const mes = (data.getMonth() + 1).toString().padStart(2, '0');
    const dia = data.getDate().toString().padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }
}
